import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getProjects, createProject, updateProject, deleteProject, getClients, getEmployees } from '../lib/api';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ActionButton,
  ActionGroup,
  AppDialog,
  DataTable,
  EmptyState,
  PageToolbar,
  StatusBadge,
} from '../components/app';
import { ProjectForm, createProjectColumns } from '../components/projects';
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  Briefcase,
  CalendarBlank,
  Users,
  CurrencyDollar,
  Clock,
  Kanban,
  ListBullets,
  CalendarDots,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export function ProjectsPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_id: '',
    project_type: 'desarrollo',
    status: 'planeacion',
    manager_id: '',
    team_ids: [],
    start_date: '',
    end_date: '',
    budget: 0,
    estimated_hours: 0,
    priority: 'media',
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const [projectsRes, clientsRes, employeesRes] = await Promise.all([
        getProjects(params),
        getClients().catch(() => ({ data: [] })),
        getEmployees().catch(() => ({ data: [] })),
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await updateProject(editingProject._id, formData);
      } else {
        await createProject(formData);
      }
      toast.success(t('successSaved'));
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name || '',
      description: project.description || '',
      client_id: project.client_id || '',
      project_type: project.project_type || 'desarrollo',
      status: project.status || 'planeacion',
      manager_id: project.manager_id || '',
      team_ids: project.team_ids || [],
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      budget: project.budget || 0,
      estimated_hours: project.estimated_hours || 0,
      priority: project.priority || 'media',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        await deleteProject(id);
        toast.success(t('successDeleted'));
        fetchData();
      } catch (error) {
        toast.error(t('errorOccurred'));
      }
    }
  };

  const resetForm = () => {
    setEditingProject(null);
    setFormData({
      name: '',
      description: '',
      client_id: '',
      project_type: 'desarrollo',
      status: 'planeacion',
      manager_id: '',
      team_ids: [],
      start_date: '',
      end_date: '',
      budget: 0,
      estimated_hours: 0,
      priority: 'media',
    });
  };

  const filteredProjects = projects.filter(proj => {
    return proj.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      proj.description?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getClientName = (clientId) => {
    const client = clients.find(c => c._id === clientId);
    return client?.name || 'No client';
  };

  const calculateProgress = (project) => {
    if (!project.estimated_hours || project.estimated_hours === 0) return 0;
    return Math.min(((project.actual_hours || 0) / project.estimated_hours) * 100, 100);
  };

  const projectActions = (project) => (
    ['admin', 'gerencia', 'pm'].includes(user?.role) ? (
      <ActionGroup className="gap-1">
        <ActionButton
          icon={PencilSimple}
          tone="edit"
          iconOnly
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(project);
          }}
        />
        <ActionButton
          icon={Trash}
          tone="delete"
          iconOnly
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(project._id);
          }}
        />
      </ActionGroup>
    ) : null
  );

  const projectColumns = createProjectColumns({
    t,
    getClientName,
    calculateProgress,
    projectActions,
  });

  return (
    <Layout title={t('projects')}>
      <div className="space-y-6" data-testid="projects-page">
        {/* Header */}
        <PageToolbar
          filters={
            <>
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 valhalla-input"
                data-testid="project-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 valhalla-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#151B26] border-[#242E42]">
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="planeacion">{t('planning')}</SelectItem>
                <SelectItem value="activo">{t('active')}</SelectItem>
                <SelectItem value="en_pausa">{t('paused')}</SelectItem>
                <SelectItem value="cerrado">{t('closed')}</SelectItem>
                <SelectItem value="cancelado">{t('cancelled')}</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex border border-[#242E42] rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-[#E2B05E] text-[#0B0E14]' : 'text-[#9CA3AF] hover:text-[#F3F4F6]'}`}
              >
                <Kanban className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-[#E2B05E] text-[#0B0E14]' : 'text-[#9CA3AF] hover:text-[#F3F4F6]'}`}
              >
                <ListBullets className="w-4 h-4" />
              </button>
            </div>
            </>
          }
          actions={
            <AppDialog
              open={isDialogOpen}
              onOpenChange={(open) => {
                setIsDialogOpen(open);
                if (!open) resetForm();
              }}
              size="lg"
              title={editingProject ? t('edit') : t('newProject')}
              trigger={
                ['admin', 'gerencia', 'pm'].includes(user?.role) ? (
                  <ActionButton
                    icon={Plus}
                    label={t('newProject')}
                    tone="primary"
                    className="font-semibold"
                    data-testid="new-project-btn"
                  />
                ) : null
              }
            >
              <ProjectForm
                formData={formData}
                setFormData={setFormData}
                clients={clients}
                t={t}
                onSubmit={handleSubmit}
                onCancel={() => setIsDialogOpen(false)}
              />
            </AppDialog>
          }
        />

        {/* Projects Grid/List */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProjects.map((project) => (
              <div 
                key={project._id} 
                className="valhalla-card p-5 cursor-pointer hover:border-[#E2B05E]/50 transition-all" 
                data-testid={`project-card-${project._id}`}
                onClick={(e) => {
                  // Only navigate if not clicking on action buttons
                  if (!e.target.closest('button')) {
                    navigate(`/projects/${project._id}`);
                  }
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#F3F4F6] mb-1">{project.name}</h3>
                    <p className="text-sm text-[#9CA3AF] line-clamp-2">{project.description || 'No description'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <StatusBadge value={project.status} />
                  <StatusBadge value={project.priority} />
                </div>

                <div className="space-y-2 text-sm mb-4">
                  <div className="flex items-center gap-2 text-[#9CA3AF]">
                    <Users className="w-4 h-4" />
                    <span>{getClientName(project.client_id)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#9CA3AF]">
                    <CalendarBlank className="w-4 h-4" />
                    <span>{project.start_date || 'No date'} - {project.end_date || 'No date'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#9CA3AF]">
                    <CurrencyDollar className="w-4 h-4" />
                    <span>${project.budget?.toLocaleString() || 0}</span>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="flex items-center justify-between text-xs text-[#9CA3AF] mb-1">
                    <span>{t('progress')}</span>
                    <span>{calculateProgress(project).toFixed(0)}%</span>
                  </div>
                  <Progress value={calculateProgress(project)} className="h-2 bg-[#242E42]" />
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-[#242E42]">
                  <div className="flex items-center gap-1 text-sm text-[#9CA3AF]">
                    <Clock className="w-4 h-4" />
                    <span>{project.actual_hours || 0}/{project.estimated_hours || 0}h</span>
                  </div>
                  {/* Solo admin, gerencia, pm pueden editar/eliminar */}
                  {(['admin', 'gerencia', 'pm'].includes(user?.role)) && (
                    projectActions(project)
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <DataTable
            columns={projectColumns}
            data={filteredProjects}
            getRowKey={(project) => project._id}
            loading={loading}
            loadingLabel={t('loading')}
            emptyIcon={Briefcase}
            emptyDescription={t('noData')}
            mobileTitle={(project) => project.name}
            mobileSubtitle={(project) => getClientName(project.client_id)}
            mobileBadge={(project) => <StatusBadge value={project.status} />}
            mobileActions={(project) => (
              <>
                <ActionButton
                  label="Ver"
                  tone="view"
                  onClick={() => navigate(`/projects/${project._id}`)}
                />
                {projectActions(project)}
              </>
            )}
          />
        )}

        {viewMode === 'grid' && filteredProjects.length === 0 && !loading && (
          <EmptyState icon={Briefcase} description={t('noData')} />
        )}
      </div>
    </Layout>
  );
}

export default ProjectsPage;
